export type RecordValue = Record<string, unknown>

export type NormalizedPipelineSyncStatus =
  | 'in_progress'
  | 'error'
  | 'success'
  | 'warning'
  | null

export type NormalizedPipelineStage =
  | 'Launched'
  | 'Closed Lost'
  | 'Prospect'
  | 'Business Validation'
  | 'Committed'
  | 'Technical Validation'
  | 'Qualified'
  | 'Open'
  | 'Research'
  | 'Disqualified'

export interface NormalizedPipelineMetrics {
  total: number
  won: number
  lost: number
  active: number
  expiring: number
  mrr: number
  mrrCurrency: string | null
}

export const isRecord = (value: unknown): value is RecordValue =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const getErrorValueMessage = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim()) return value.trim()
  if (Array.isArray(value)) {
    const messages = value
      .map(getErrorValueMessage)
      .filter((message): message is string => !!message)
    return messages.length ? messages.join(', ') : null
  }
  if (isRecord(value)) {
    return getErrorValueMessage(value.message ?? value.detail ?? value.error)
  }
  return null
}

/** Preserves structured API messages before falling back to transport errors. */
export const getPipelineErrorMessage = (
  error: unknown,
  fallback: string
): string => {
  if (isRecord(error)) {
    const response = isRecord(error.response) ? error.response : null
    const responseData = response ? response.data : null
    const structuredMessage = getErrorValueMessage(
      isRecord(responseData)
        ? (responseData.errors ?? responseData.message ?? responseData.detail)
        : responseData
    )
    if (structuredMessage) return structuredMessage
  }
  if (error instanceof Error && error.message) return error.message
  return fallback
}

export const toStringOrNull = (value: unknown): string | null => {
  if (typeof value !== 'string' && typeof value !== 'number') return null
  const result = String(value).trim()
  return result ? result : null
}

export const toNumberOrNull = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

export const toCount = (value: unknown, fallback = 0): number =>
  toNumberOrNull(value) ?? fallback

export const getField = (
  record: RecordValue,
  ...keys: readonly string[]
): unknown => {
  for (const key of keys) {
    if (record[key] !== undefined && record[key] !== null) return record[key]
  }
  return null
}

export const getReferralList = (
  payload: unknown,
  providerLabel = 'Pipeline'
): unknown[] => {
  if (Array.isArray(payload)) return payload
  const response =
    isRecord(payload) && isRecord(payload.data) ? payload.data : payload
  if (!isRecord(response)) {
    throw new Error(`${providerLabel} referrals response is malformed.`)
  }
  if (Array.isArray(response.referrals)) return response.referrals
  if (Array.isArray(response.data)) return response.data
  throw new Error(`${providerLabel} referrals response is missing referrals.`)
}

const PIPELINE_SYNC_STATUS: Record<
  Exclude<NormalizedPipelineSyncStatus, null>,
  true
> = {
  in_progress: true,
  error: true,
  success: true,
  warning: true,
}

/** Converts provider status spellings to the shared synchronization union. */
export const normalizeSyncStatus = (
  value: unknown
): NormalizedPipelineSyncStatus => {
  const status = toStringOrNull(value)
    ?.toLowerCase()
    .replace(/[\s-]+/g, '_')
  if (!status) return null
  if (
    status === 'awaiting_write' ||
    status === 'awaiting_write_results' ||
    status === 'awaiting_transformation'
  ) {
    return 'in_progress'
  }
  if (status === 'failed') return 'error'
  if (status === 'successful') return 'success'
  return PIPELINE_SYNC_STATUS[
    status as Exclude<NormalizedPipelineSyncStatus, null>
  ]
    ? (status as Exclude<NormalizedPipelineSyncStatus, null>)
    : null
}

const PIPELINE_STAGES: Record<NormalizedPipelineStage, true> = {
  Launched: true,
  'Closed Lost': true,
  Prospect: true,
  'Business Validation': true,
  Committed: true,
  'Technical Validation': true,
  Qualified: true,
  Open: true,
  Research: true,
  Disqualified: true,
}

const normalizeStageKey = (value: string): string =>
  value.toLowerCase().replace(/\s+/g, ' ').trim()

export const normalizeStage = (
  value: unknown
): NormalizedPipelineStage | null => {
  const stage = toStringOrNull(value)
  if (!stage) return null
  const normalized = Object.keys(PIPELINE_STAGES).find(
    candidate => normalizeStageKey(candidate) === normalizeStageKey(stage)
  )
  return (normalized as NormalizedPipelineStage | undefined) || null
}

const PIPELINE_METRIC_KEYS = [
  'total',
  'won',
  'lost',
  'active',
  'expiring',
  'mrr',
] as const

export const normalizeMetrics = (
  payload: unknown,
  providerLabel = 'Pipeline'
): NormalizedPipelineMetrics => {
  if (!isRecord(payload)) {
    throw new Error(`${providerLabel} metrics response is malformed.`)
  }

  const pipeline = isRecord(payload.pipelineMetrics)
    ? payload.pipelineMetrics
    : isRecord(payload.pipeline_metrics)
      ? payload.pipeline_metrics
      : payload

  if (
    !PIPELINE_METRIC_KEYS.every(
      key =>
        toNumberOrNull(
          pipeline[key] ?? pipeline[`${key}_count`] ?? pipeline[`${key}Count`]
        ) !== null
    )
  ) {
    throw new Error(
      `${providerLabel} metrics response is missing pipeline metrics.`
    )
  }

  const mrrCurrency = toStringOrNull(
    pipeline.mrr_currency ??
      pipeline.mrrCurrency ??
      pipeline.currency ??
      payload.mrr_currency ??
      payload.mrrCurrency ??
      payload.currency
  )

  return {
    total: toCount(
      pipeline.total ?? pipeline.total_count ?? pipeline.totalCount
    ),
    won: toCount(pipeline.won ?? pipeline.won_count ?? pipeline.wonCount),
    lost: toCount(pipeline.lost ?? pipeline.lost_count ?? pipeline.lostCount),
    active: toCount(
      pipeline.active ?? pipeline.active_count ?? pipeline.activeCount
    ),
    expiring: toCount(
      pipeline.expiring ?? pipeline.expiring_count ?? pipeline.expiringCount
    ),
    mrr: toCount(pipeline.mrr),
    mrrCurrency,
  }
}
